import React, { useEffect, useRef } from 'react';
import { User } from 'firebase/auth';
import { StoryBible, AppStatus } from '../src/types';
import { resolveApiBaseUrl } from '../src/utils/apiBase';
import Tooltip from './Tooltip';
import { isProjectUnlocked } from '../src/utils/entitlements';
import type { ManuscriptTier } from '../src/utils/manuscriptCreditRouting.service';

const ADMIN_UIDS = ['ADMIN_TEST_UID', 'skald-scribe-admin', 'your-admin-uid-here', '4WdZq8521c7', '4WdZq8521c7P'];
const isAdmin = (user: any) => user && (ADMIN_UIDS.includes(user.uid) || user.email === 'skaldhaven@gmail.com');

interface UserData {
  tokens: number;
  subscriptionTier: 'free' | 'pro';
  email: string;
  standardCredits?: number;
  longCredits?: number;
  epicCredits?: number;
  rewriteCredits?: number;
  rewriteCreditsBalance?: number;
  manuscriptCreditBalances?: {
    short_manuscript?: number;
    standard_manuscript?: number;
    large_manuscript?: number;
    epic_manuscript?: number;
  };
}

interface PaywallModalProps {
  isOpen: boolean;
  reason: 'generation' | 'autowrite' | 'export' | 'sequel' | 'rewrite';
  highlightedTier?: ManuscriptTier;
  headline?: string;
  subheadline?: string;
  onClose: () => void;
  user: User | null;
  userData: UserData | null;
  updateUserData: (data: Partial<UserData>) => Promise<void>;
  refreshUserData?: () => Promise<void>;
  applyManuscriptCreditToProject?: (projectId: string, tier: string, isMock?: boolean) => Promise<boolean>;
  storyBible: StoryBible | null;
  setStoryBible: React.Dispatch<React.SetStateAction<StoryBible | null>>;
  currentProjectId: string | null;
  addLog: (agent: any, message: string, type?: any) => void;
  saveCurrentProject: (durable?: boolean) => void;
  setStatus: (status: AppStatus) => void;
  setIsAutoRunning: (val: boolean) => void;
}

export const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  reason,
  highlightedTier,
  headline,
  subheadline,
  onClose,
  user,
  userData,
  updateUserData,
  refreshUserData,
  applyManuscriptCreditToProject,
  storyBible,
  setStoryBible,
  currentProjectId,
  addLog,
  saveCurrentProject,
  setStatus,
  setIsAutoRunning,
}) => {
  const checkoutInProgressRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !refreshUserData) return;
    void refreshUserData();
  }, [isOpen, refreshUserData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-cv-page/85 backdrop-blur-md flex items-center justify-center p-4 pb-24 md:pb-4">
      <div className="bg-cv-panel border border-cv-border-strong rounded-2xl p-6 md:p-8 max-w-2xl w-full shadow-2xl relative max-h-[85vh] overflow-y-auto">
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-cv-muted hover:text-cv-primary transition-colors p-2 bg-cv-elevated rounded-lg border border-cv-border-strong"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <header className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[10px] font-mono font-black text-cv-gold uppercase tracking-widest">
              Chronovela Manuscript Credits
            </div>
            <Tooltip text="Manuscript Credits create books. Rewrite Credits revise chapters." />
          </div>
          <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter">
            {reason === 'rewrite'
              ? 'Rewrite Limit Exceeded'
              : headline || 'Manuscript Credits create books. Rewrite Credits revise chapters.'}
          </h2>
          <p className="text-cv-muted text-xs mt-1">
            {reason === 'rewrite'
              ? 'This project has exhausted its included rewrites. Purchase a Rewrite Pack to unlock more edits.'
              : subheadline || 'Choose the manuscript size that matches your selected chapter count.'}
          </p>
        </header>

        {/* Current Balances Panel */}
        <div className="bg-cv-page border border-cv-border rounded-xl p-4 mb-6 grid grid-cols-2 md:grid-cols-5 gap-2 text-center font-mono">
          <div>
            <div className="text-[9px] text-cv-muted uppercase font-black">SHORT MANUSCRIPT</div>
            <div className="text-lg font-black text-cv-primary mt-0.5">{userData?.manuscriptCreditBalances?.short_manuscript || 0}</div>
          </div>
          <div>
            <div className="text-[9px] text-cv-muted uppercase font-black">STANDARD MANUSCRIPT</div>
            <div className="text-lg font-black text-cv-primary mt-0.5">{userData?.manuscriptCreditBalances?.standard_manuscript || 0}</div>
          </div>
          <div>
            <div className="text-[9px] text-cv-muted uppercase font-black">LARGE MANUSCRIPT</div>
            <div className="text-lg font-black text-cv-primary mt-0.5">{userData?.manuscriptCreditBalances?.large_manuscript || 0}</div>
          </div>
          <div>
            <div className="text-[9px] text-cv-muted uppercase font-black">EPIC MANUSCRIPT</div>
            <div className="text-lg font-black text-cv-primary mt-0.5">{userData?.manuscriptCreditBalances?.epic_manuscript || 0}</div>
          </div>
          <div>
            <div className="text-[9px] text-cv-muted uppercase font-black">Rewrite Credits</div>
            <div className="text-lg font-black text-cv-primary mt-0.5">{userData?.rewriteCreditsBalance || 0}</div>
          </div>
        </div>

        {/* Helper note */}
        <p className="text-[11px] text-cv-muted font-mono mb-3 px-1">
          💡 Owned credits are stored in your account. Apply one to unlock this project.
        </p>

        {/* Target Options List */}
        <div className="space-y-3 mb-6">
          {[
            { 
              tier: 'short_manuscript', 
              title: 'Short Manuscript Credit', 
              desc: 'Creates one manuscript up to 10 chapters. Includes export and 2 rewrite credits.',
              price: '$14.99',
              maxChapters: 10,
              rewritesIncluded: 2,
              userBalance: userData?.manuscriptCreditBalances?.short_manuscript ?? 0
            },
            { 
              tier: 'standard_manuscript', 
              title: 'Standard Manuscript Credit', 
              desc: 'Creates one manuscript up to 20 chapters. Includes export and 5 rewrite credits.',
              price: '$29.99',
              maxChapters: 20,
              rewritesIncluded: 5,
              userBalance: userData?.manuscriptCreditBalances?.standard_manuscript ?? 0
            },
            { 
              tier: 'large_manuscript', 
              title: 'Large Manuscript Credit', 
              desc: 'Creates one manuscript up to 30 chapters. Includes export and 8 rewrite credits.',
              price: '$44.99',
              maxChapters: 30,
              rewritesIncluded: 8,
              userBalance: userData?.manuscriptCreditBalances?.large_manuscript ?? 0
            },
            { 
              tier: 'epic_manuscript', 
              title: 'Epic Manuscript Credit', 
              desc: 'Creates one manuscript up to 40 chapters. Includes export and 10 rewrite credits.',
              price: '$59.99',
              maxChapters: 40,
              rewritesIncluded: 10,
              userBalance: userData?.manuscriptCreditBalances?.epic_manuscript ?? 0
            },
            { 
              tier: 'rewrite_pack_small', 
              title: 'Small Rewrite Pack', 
              desc: 'Adds 5 rewrite credits.',
              price: '$4.99',
              maxChapters: 0,
              rewritesIncluded: 0,
              userBalance: userData?.rewriteCreditsBalance ?? 0
            },
            { 
              tier: 'rewrite_pack_author', 
              title: 'Author Rewrite Pack', 
              desc: 'Adds 15 rewrite credits.',
              price: '$9.99',
              maxChapters: 0,
              rewritesIncluded: 0,
              userBalance: userData?.rewriteCreditsBalance ?? 0
            },
            { 
              tier: 'rewrite_pack_heavy', 
              title: 'Heavy Rewrite Pack', 
              desc: 'Adds 35 rewrite credits.',
              price: '$19.99',
              maxChapters: 0,
              rewritesIncluded: 0,
              userBalance: userData?.rewriteCreditsBalance ?? 0
            }
          ].map((opt) => {
            const isRewriteOption = opt.tier.startsWith('rewrite_pack_');
            const ownedCount = opt.userBalance;

            // APPLIED if project has any valid paid tier that covers this option's maxChapters
            const isProjectPaidTier = storyBible?.creditTier === 'standard_manuscript' || storyBible?.creditTier === 'large_manuscript' || storyBible?.creditTier === 'epic_manuscript';
            const projectMaxChapters = storyBible?.maxChaptersAllowed || 0;
            
            const isAppliedToProject =
              isProjectPaidTier &&
              storyBible?.exportUnlocked === true &&
              projectMaxChapters >= opt.maxChapters;

            if (import.meta.env.DEV) {
              console.log(`[TRACE 4] Paywall logic for ${opt.tier}:`, {
              creditTier: storyBible?.creditTier,
              exportUnlocked: storyBible?.exportUnlocked,
              paidEntitlementId: storyBible?.paidEntitlementId,
              maxChaptersAllowed: projectMaxChapters,
              isAppliedToProject: isAppliedToProject,
              reasonIfFalse: !isProjectPaidTier ? 'Not a paid tier' : (!storyBible?.exportUnlocked ? 'Export not unlocked' : (projectMaxChapters < opt.maxChapters ? 'Max chapters too low' : 'N/A'))
            });
            }

            // Another paid tier is already applied (a different one)
            const isOtherTierApplied = isProjectUnlocked(storyBible) && !isAppliedToProject;

            // This tier is applicable if user owns one and project isn't already locked to another tier
            const canApply = ownedCount > 0 && !isAppliedToProject && !isOtherTierApplied;

            const isHighlighted = !isRewriteOption && highlightedTier === opt.tier;

            return (
              <div
                key={opt.tier}
                className={`bg-cv-panel/50 border rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${
                  isHighlighted
                    ? 'border-amber-500/80 ring-2 ring-amber-500/40 bg-amber-950/20'
                    : 'border-cv-border'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm text-cv-primary">{opt.title}</h3>
                    <span className="text-[10px] text-cv-muted font-mono">({opt.price})</span>
                  </div>
                  <p className="text-[11px] text-cv-muted mt-1">{opt.desc}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 self-stretch md:self-auto justify-end">

                  {/* BUY button — always visible */}
                  <button
                    onClick={async () => {
                      if (!user || checkoutInProgressRef.current) return;
                      checkoutInProgressRef.current = true;
                      try {
                        const idToken = await user.getIdToken();
                        const API_BASE =
                          (import.meta as any).env?.VITE_API_BASE_URL ||
                          (typeof window !== 'undefined' && window.location.origin) ||
                          'http://localhost:3001';
                        
                        const res = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${idToken}`
                          },
                          body: JSON.stringify({ creditType: opt.tier })
                        });
                        if (!res.ok) {
                          const errData = await res.json().catch(() => ({}));
                          throw new Error(errData.error || 'Failed to create checkout session');
                        }
                        const data = await res.json();
                        if (data.url) {
                          window.location.href = data.url;
                        }
                      } catch (e: any) {
                        console.error(opt.tier, e);
                        alert(`Checkout Error: ${e.message}`);
                      } finally {
                        checkoutInProgressRef.current = false;
                      }
                    }}
                    className="px-4 py-2 text-xs font-black uppercase rounded-lg bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page shadow-md transition-all"
                  >
                    Buy
                  </button>

                  {/* OWNED label — always visible for manuscript tiers */}
                  {!isRewriteOption && (
                    <span className={`px-3 py-2 text-xs font-black uppercase rounded-lg border font-mono ${
                      ownedCount > 0
                        ? 'bg-cv-elevated text-cv-interactive border-blue-800'
                        : 'bg-cv-panel text-cv-disabled border-cv-border'
                    }`}>
                      OWNED: {ownedCount}
                    </span>
                  )}

                  {/* APPLY CREDIT / APPLIED — only for manuscript tiers */}
                  {!isRewriteOption && (
                    isAppliedToProject ? (
                      <button
                        disabled
                        className="px-4 py-2 text-xs font-black uppercase rounded-lg bg-emerald-900 text-cv-success border border-emerald-700 cursor-default"
                      >
                        APPLIED
                      </button>
                    ) : canApply ? (
                      <button
                        onClick={async () => {
                          if (!userData || !storyBible || !currentProjectId) return;
                          if (applyManuscriptCreditToProject) {
                            try {
                              const verifiedEntitlement = await applyManuscriptCreditToProject(currentProjectId, opt.tier, false);
                              if (verifiedEntitlement) {
                                addLog('System', `Applied 1 ${opt.title} to project. Project upgraded to ${opt.title.replace(' Credit', '')}.`, 'success');

                                setStoryBible(prev => prev ? {
                                  ...prev,
                                  ...verifiedEntitlement
                                } : null);

                                setTimeout(() => {
                                  refreshUserData && refreshUserData();
                                }, 0);

                                onClose();
                              } else {
                                alert("Failed to apply credit. Please try again.");
                              }
                            } catch (error: any) {
                              alert(error.message || "Failed to apply credit. Your balance was not charged.");
                            }
                          }
                        }}
                        className="px-4 py-2 text-xs font-black uppercase rounded-lg bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page shadow-md transition-all"
                      >
                        APPLY CREDIT
                      </button>
                    ) : null
                  )}

                  {/* Rewrite balance label */}
                  {isRewriteOption && (
                    <div className="text-right ml-2">
                      <span className="text-[10px] font-mono font-bold text-cv-muted uppercase">Available: {opt.userBalance}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>


        {/* Developer Mock Billing Console (Admin Only / Debug) */}
        {user && isAdmin(user) && (
          <div className="mt-8 border-t border-cv-border pt-6">
            <h3 className="text-xs font-black text-cv-gold uppercase tracking-widest mb-4 flex items-center gap-2">
              🛠️ Developer Billing Tools (Mock Grants)
            </h3>
            <p className="text-[10px] text-cv-muted font-mono mb-4 leading-relaxed">
              Admins and developers can use these tools to test Manuscript Credits, Rewrite Credits, and project entitlement states. These controls are hidden from public users.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {[
                { field: 'manuscriptCreditBalances.short_manuscript', label: '+1 Short Manuscript Credit', grantType: 'short_manuscript', amount: 1 },
                { field: 'manuscriptCreditBalances.standard_manuscript', label: '+1 Standard Manuscript Credit', grantType: 'standard_manuscript', amount: 1 },
                { field: 'manuscriptCreditBalances.large_manuscript', label: '+1 Large Manuscript Credit', grantType: 'large_manuscript', amount: 1 },
                { field: 'manuscriptCreditBalances.epic_manuscript', label: '+1 Epic Manuscript Credit', grantType: 'epic_manuscript', amount: 1 },
                { field: 'rewriteCreditsBalance', label: '+5 Rewrite Credits', grantType: 'rewrite_pack_small', amount: 5 },
                { field: 'rewriteCreditsBalance', label: '+15 Rewrite Credits', grantType: 'rewrite_pack_author', amount: 15 },
                { field: 'rewriteCreditsBalance', label: '+35 Rewrite Credits', grantType: 'rewrite_pack_heavy', amount: 35 }
              ].map((act) => (
                <button
                  key={act.grantType}
                  onClick={async () => {
                    try {
                      const idToken = await user.getIdToken();
                      const API_BASE = resolveApiBaseUrl();

                      const reqBody = {
                        grantType: act.grantType,
                        amount: act.amount,
                        targetUserUid: user.uid
                      };

                      const res = await fetch(`${API_BASE}/api/tokens/grant`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${idToken}`
                        },
                        body: JSON.stringify(reqBody)
                      });

                      if (!res.ok) {
                        let msg = res.statusText;
                        try {
                          const errData = await res.json();
                          msg = errData.message || errData.details || msg;
                        } catch (_) {}
                        throw new Error(`Server denied grant: ${msg}`);
                      }

                      // The frontend FirebaseProvider uses a one-time getDoc rather than onSnapshot for user data,
                      // so we use refreshUserData to fetch the accurately updated balances from the database instantly.
                      addLog('System', `Admin Mock Grant: requested +${act.amount} via API to ${act.field}. Syncing...`, 'info');
                      if (refreshUserData) {
                        await refreshUserData();
                      }
                    } catch (err: any) {
                      console.error("Admin grant failed validation:", err);
                      alert(`Verification failed: ${err.message || err}`);
                    }
                  }}
                  className="py-2 bg-cv-elevated hover:bg-cv-elevated text-cv-secondary font-mono text-[10px] rounded border border-cv-border-strong transition-all font-bold"
                >
                  {act.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <button
                onClick={async () => {
                  if (!storyBible) return;
                  setStoryBible(prev => prev ? { 
                    ...prev, 
                    creditTier: 'free_preview', 
                    maxChaptersAllowed: 1, 
                    exportUnlocked: false,
                    paidEntitlementId: null,
                    entitlementAppliedAt: null,
                    rewriteCreditsIncluded: 0,
                    rewriteCreditsUsed: 0,
                    freePreviewUsed: false
                  } : null);
                  addLog('System', 'Mock Adjust: reset project creditTier to free_preview', 'warning');
                  setTimeout(() => saveCurrentProject(true), 0);
                }}
                className="py-2 bg-red-950/20 hover:bg-red-950/40 text-cv-danger font-mono text-[10px] rounded border border-cv-danger/20 transition-all font-bold"
              >
                Reset Active Project to Free Preview
              </button>
                <button
                  onClick={async () => {
                    if (!storyBible || !currentProjectId) return;
                    if (applyManuscriptCreditToProject) {
                      const entitlementId = await applyManuscriptCreditToProject(currentProjectId, 'standard_manuscript', true);
                      if (entitlementId) {
                        addLog('System', 'Mock Adjust: upgraded project creditTier to standard_manuscript', 'success');
                        
                        setStoryBible(prev => prev ? {
                           ...prev,
                           creditTier: 'standard_manuscript',
                           maxChaptersAllowed: 20,
                           exportUnlocked: true,
                           paidEntitlementId: entitlementId,
                           rewriteCreditsIncluded: 5,
                           rewriteCreditsUsed: 0
                        } : null);

                        setTimeout(() => {
                           refreshUserData && refreshUserData();
                           saveCurrentProject(true);
                        }, 0);
                      }
                    }
                  }}
                className="py-2 bg-cv-interactive-soft hover:bg-cv-interactive/20 text-cv-gold font-mono text-[10px] rounded border border-cv-interactive/20 transition-all font-bold"
              >
                Upgrade Active Project to Standard Manuscript
              </button>
              <button
                onClick={async () => {
                  if (!storyBible) return;
                  setStoryBible(prev => prev ? { ...prev, rewriteCreditsUsed: 0 } : null);
                  addLog('System', 'Mock Adjust: reset project rewriteCreditsUsed to 0', 'success');
                  setTimeout(() => saveCurrentProject(true), 0);
                }}
                className="py-2 bg-green-950/20 hover:bg-green-950/40 text-green-400 font-mono text-[10px] rounded border border-green-500/20 transition-all font-bold"
              >
                Reset Rewrite Credits to 0
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
